/**
 * Eingabefeld mit Vollständigkeitsprüfung, und die Rückfrage dazu.
 *
 * Der Unterschied, um den es geht: `required` erzwingt, was ohne das
 * Speichern scheitern würde. Die meisten Felder eines Spielgeräts sind
 * technisch optional — Hersteller, Baujahr, Fallhöhe. Fehlen sie, wird
 * trotzdem gespeichert, und der Mangel fällt Monate später auf, wenn im
 * Bericht eine Spalte leer bleibt und niemand mehr weiß, wie hoch die
 * Schaukel war.
 *
 * Deshalb der Mittelweg: beim Abschicken wird gezeigt, was fehlt, und
 * gefragt, ob es trotzdem so gespeichert werden soll. Kein Zwang — auf
 * einem alten Platz ist der Hersteller manchmal wirklich nicht mehr
 * feststellbar. Aber auch kein stilles Durchwinken.
 */

import type { ReactNode } from 'react'

export type Feldangabe = {
  /** Name im Formular, wie er zum Server geht. */
  name: string
  beschriftung: string
  /** Warum das Feld gebraucht wird. Steht in der Rückfrage. */
  warum?: string
}

/**
 * Welche der erwarteten Felder leer geblieben sind.
 *
 * Gelesen wird aus dem Formular selbst und nicht aus einem Zustand: die
 * Formulare hier sind unkontrolliert, und zwei Quellen für denselben Wert
 * laufen früher oder später auseinander.
 */
export function offeneFelder(
  formular: HTMLFormElement,
  erwartet: Feldangabe[],
): Feldangabe[] {
  const werte = new FormData(formular)
  return erwartet.filter((f) => String(werte.get(f.name) ?? '').trim() === '')
}

export function Feld({
  name,
  beschriftung,
  hinweis,
  fehlt,
  breit,
  children,
  ...rest
}: {
  name: string
  beschriftung: string
  hinweis?: string
  /** Ist dieses Feld gerade als fehlend markiert? */
  fehlt?: boolean
  breit?: boolean
  children?: ReactNode
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const kennung = `tf-${name}`
  return (
    <div className={breit ? 'sm:col-span-2' : undefined}>
      <label htmlFor={kennung}>
        {beschriftung}
        {fehlt && (
          <span className="ml-2 font-normal text-stufe-2">— fehlt noch</span>
        )}
      </label>
      {children ?? (
        <input
          id={kennung}
          name={name}
          className={fehlt ? 'border-stufe-2 bg-stufe-2-grund' : undefined}
          {...rest}
        />
      )}
      {hinweis && <p className="mt-1 text-base text-slate-600">{hinweis}</p>}
    </div>
  )
}

/**
 * Die Rückfrage.
 *
 * Sie nennt die Felder beim Namen und sagt, wofür sie gebraucht werden.
 * „Einige Felder sind leer“ wäre keine Auskunft — dann klickt man auf
 * „Trotzdem“, ohne hinzusehen.
 */
export function Nachfrage({
  offen,
  trotzdem,
  zurueck,
  laeuft,
}: {
  offen: Feldangabe[]
  trotzdem: () => void
  zurueck: () => void
  laeuft?: boolean
}) {
  return (
    <div
      role="alertdialog"
      aria-label="Angaben unvollständig"
      className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3"
    >
      <p className="font-bold text-stufe-2">
        {offen.length === 1
          ? 'Eine Angabe fehlt noch'
          : `${offen.length} Angaben fehlen noch`}
      </p>

      <ul className="mt-2 space-y-1">
        {offen.map((f) => (
          <li key={f.name} className="text-base">
            <span className="font-semibold">{f.beschriftung}</span>
            {f.warum && <span className="text-slate-700"> — {f.warum}</span>}
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="knopf-zweit px-3" onClick={zurueck}>
          Ergänzen
        </button>
        <button
          type="button"
          className="knopf-still px-3"
          onClick={trotzdem}
          disabled={laeuft}
        >
          {laeuft ? 'Speichert …' : 'Trotzdem speichern'}
        </button>
      </div>
    </div>
  )
}
